import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const { Option } = Select;

const Academies = () => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingAcademy, setEditingAcademy] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: academiesData, isLoading } = useQuery('academies', async () => {
        const response = await api.get('/superadmin/academies');
        return response.data.data;
    });

    const createMutation = useMutation(
        (data) => api.post('/superadmin/academies', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('academies');
                message.success('Academy created successfully');
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to create academy');
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }) => api.put(`/superadmin/academies/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('academies');
                message.success('Academy updated successfully');
                setIsModalVisible(false);
                setEditingAcademy(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to update academy');
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => api.delete(`/superadmin/academies/${id}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('academies');
                message.success('Academy deleted successfully');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to delete academy');
            },
        }
    );

    const handleSubmit = (values) => {
        const formattedValues = {
            ...values,
            address: {
                street: values.street,
                city: values.city,
                state: values.state,
                country: values.country,
                zipCode: values.zipCode,
            },
            subscription: {
                plan: values.plan || 'basic',
            },
        };

        // Remove flat fields
        delete formattedValues.street;
        delete formattedValues.city;
        delete formattedValues.state;
        delete formattedValues.country;
        delete formattedValues.zipCode;
        delete formattedValues.plan;

        if (editingAcademy) {
            updateMutation.mutate({ id: editingAcademy._id, data: formattedValues });
        } else {
            createMutation.mutate(formattedValues);
        }
    };

    const handleEdit = (record) => {
        setEditingAcademy(record);
        form.setFieldsValue({
            ...record,
            street: record.address?.street,
            city: record.address?.city,
            state: record.address?.state,
            country: record.address?.country,
            zipCode: record.address?.zipCode,
            plan: record.subscription?.plan,
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('actionCannotBeUndone'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const columns = [
        { title: t('code'), dataIndex: 'code', key: 'code', width: 100 },
        { title: t('name'), dataIndex: 'name', key: 'name' },
        { title: t('email'), dataIndex: 'email', key: 'email' },
        { title: t('phone'), dataIndex: 'phone', key: 'phone' },
        {
            title: t('subscription'),
            key: 'subscription',
            render: (_, r) => {
                const plan = r.subscription?.plan || 'basic';
                const color = { basic: 'default', standard: 'blue', premium: 'gold' }[plan];
                return <Tag color={color}>{plan.toUpperCase()}</Tag>;
            },
        },
        {
            title: t('status'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>{isActive ? t('active').toUpperCase() : t('inactive').toUpperCase()}</Tag>
            ),
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record._id)}
                        size="small"
                    />
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="page-header">
                <h1>{t('academies')}</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingAcademy(null);
                        form.resetFields();
                        setIsModalVisible(true);
                    }}
                    size="large"
                >
                    {t('addAcademy')}
                </Button>
            </div>

            <Table columns={columns} dataSource={academiesData} rowKey="_id" loading={isLoading} />

            <Modal
                title={editingAcademy ? t('editAcademy') : t('addNewAcademy')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingAcademy(null);
                    form.resetFields();
                }}
                footer={null}
                width="min(800px, 95vw)"
                centered
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Academy Name"
                                rules={[{ required: true, message: 'Please enter academy name' }]}
                            >
                                <Input placeholder="ABC Language Institute" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="code"
                                label="Academy Code"
                                rules={[
                                    { required: true, message: 'Please enter academy code' },
                                    { pattern: /^[A-Z]+$/, message: 'Only uppercase letters allowed' },
                                ]}
                            >
                                <Input placeholder="ABC" maxLength={10} size="large" style={{ textTransform: 'uppercase' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    { required: true, message: 'Please enter email' },
                                    { type: 'email', message: 'Please enter a valid email' },
                                ]}
                            >
                                <Input placeholder="info@academy.com" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label="Phone"
                                rules={[{ required: true, message: 'Please enter phone number' }]}
                            >
                                <Input placeholder="+1234567890" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="website" label="Website">
                                <Input placeholder="https://academy.com" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="plan" label="Subscription Plan" initialValue="basic">
                                <Select size="large">
                                    <Option value="basic">Basic</Option>
                                    <Option value="standard">Standard</Option>
                                    <Option value="premium">Premium</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <h3 style={{ marginTop: 24, marginBottom: 16 }}>Address</h3>

                    <Form.Item name="street" label="Street Address">
                        <Input placeholder="123 Main Street" size="large" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="city" label="City">
                                <Input placeholder="New York" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="state" label="State/Province">
                                <Input placeholder="NY" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="country" label="Country">
                                <Input placeholder="USA" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="zipCode" label="Zip Code">
                                <Input placeholder="10001" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item style={{ marginTop: 24 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" size="large" loading={createMutation.isLoading || updateMutation.isLoading}>
                                {editingAcademy ? 'Update Academy' : 'Create Academy'}
                            </Button>
                            <Button onClick={() => setIsModalVisible(false)} size="large">
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Academies;
